import { Checkbox, FormControl, FormControlLabel, FormHelperText, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import _ from 'lodash'
import moment from 'moment'

import { useErrors } from '@/hooks/ErrorsStateContext'
import { useInputState } from '@/hooks/inputStateContext'
import AddIconButton from '@/components/IconButton/AddIconButton'
import CollapsibleList from '@/components/CollapsibleList'
import DeleteIconButton from '@/components/IconButton/DeleteIconButton'

const weekdayFrom0 = [0, 1, 2, 3, 4, 5, 6]
const defaultTimeRange = { start: undefined, startMin: undefined, end: undefined, endMin: undefined, startTime: '', endTime: '' }

/**
 *
 * @param {*} props
 * @returns
 */
export default function ValidSettings (props) {
  const classes = useStyles(props)
  const [expand, setExpand] = React.useState(false)
  const [inputState, updateInputState] = useInputState()
  const [errors, setErrors] = useErrors()

  const handleChange = (e) => {
    const { name, value } = e.target
    updateInputState(name, value)
    setErrors({ ...errors, [name]: undefined })
  }

  const handleAddTimeRange = (weekday) => {
    const openings = inputState.openings ?? {}
    const timeRanges = _.cloneDeep(openings[weekday]) ?? []
    timeRanges.push(defaultTimeRange)
    if (!inputState.openings) {
      // openings 必須要是 object，若這裡沒有初始成 object 會自動變成 array
      updateInputState('openings', {})
    }
    updateInputState(['openings', weekday], timeRanges)
  }

  const handleTimeChange = (e, weekday, index) => {
    const { name, value } = e.target
    const time = value
    const hh = Number(time.split(':')[0])
    const mm = Number(time.split(':')[1])
    if (name === 'startTime') {
      updateInputState(['openings', weekday, index, 'start'], hh)
      updateInputState(['openings', weekday, index, 'startMin'], mm)
      updateInputState(['openings', weekday, index, name], value)
    }

    if (name === 'endTime') {
      updateInputState(['openings', weekday, index, 'end'], hh)
      updateInputState(['openings', weekday, index, 'endMin'], mm)
      updateInputState(['openings', weekday, index, name], value)
    }
  }

  const handle24h = (e, weekday, index) => {
    const { checked } = e.target
    if (checked) {
      updateInputState(['openings', weekday, index, 'start'], 0)
      updateInputState(['openings', weekday, index, 'startMin'], 0)
      updateInputState(['openings', weekday, index, 'end'], 0)
      updateInputState(['openings', weekday, index, 'endMin'], 0)
      updateInputState(['openings', weekday, index, 'startTime'], '00:00')
      updateInputState(['openings', weekday, index, 'endTime'], '00:00')
    } else {
      const { start, startMin, end, endMin, startTime, endTime } = defaultTimeRange
      updateInputState(['openings', weekday, index, 'start'], start)
      updateInputState(['openings', weekday, index, 'startMin'], startMin)
      updateInputState(['openings', weekday, index, 'end'], end)
      updateInputState(['openings', weekday, index, 'endMin'], endMin)
      updateInputState(['openings', weekday, index, 'startTime'], startTime)
      updateInputState(['openings', weekday, index, 'endTime'], endTime)
    }
  }

  const handleDelete = (weekday, index) => {
    const timeRanges = _.cloneDeep(inputState.openings?.[weekday])
    timeRanges.splice(index, 1)
    updateInputState(['openings', weekday], timeRanges)
  }

  return (
    <CollapsibleList title='使用時間' expand={expand} setExpand={setExpand}>
      {/* Valid From */}
      <FormControl variant='outlined' margin='dense' fullWidth>
        <TextField
          label='Valid From'
          name='validFrom'
          type='datetime-local'
          variant='outlined'
          size='small'
          fullWidth
          value={inputState.validFrom ?? ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.validFrom)}
        />
        <FormHelperText>可用日期時間:開始</FormHelperText>
      </FormControl>

      {/* Valid Until */}
      <FormControl variant='outlined' margin='dense' fullWidth>
        <TextField
          label='Valid Until'
          name='validUntil'
          type='datetime-local'
          variant='outlined'
          size='small'
          fullWidth
          value={inputState.validUntil ?? ''}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          error={Boolean(errors.validUntil)}
        />
        <FormHelperText>可用日期時間:結束</FormHelperText>
      </FormControl>

      {_.map(weekdayFrom0, (weekday) => {
        return (
          <div key={weekday}>
            <div className={classes.weekdayTitleWrap}>
              <Typography>{moment().weekday(weekday).format('dddd')}</Typography>
              <AddIconButton color='primary' onClick={() => handleAddTimeRange(weekday)} />
            </div>
            <div>
              {_.map(inputState.openings?.[weekday], (timeRange, index) => {
                const is24h = _.every([
                  timeRange.start,
                  timeRange.startMin,
                  timeRange.end,
                  timeRange.endMin,
                ], number => number === 0)

                const timeRangeKey = ['openings', weekday, index]
                const startH = _.get(inputState, [...timeRangeKey, 'start'])
                const startM = _.get(inputState, [...timeRangeKey, 'startMin'])
                const startTime = _.get(inputState, [...timeRangeKey, 'startTime']) || (
                  !_.isNil(startH) && !_.isNil(startM)
                    ? `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`
                    : ''
                )
                const endH = _.get(inputState, [...timeRangeKey, 'end'])
                const endM = _.get(inputState, [...timeRangeKey, 'endMin'])
                const endTime = _.get(inputState, [...timeRangeKey, 'endTime']) || (
                  !_.isNil(endH) && !_.isNil(endM)
                    ? `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`
                    : ''
                )

                return (
                  <div key={index} className={classes.row}>
                    <FormControl variant='outlined' margin='dense' fullWidth>
                      <TextField
                        label='Start Time'
                        name='startTime'
                        type='time'
                        variant='outlined'
                        size='small'
                        fullWidth
                        disabled={is24h}
                        value={startTime}
                        onChange={(e) => handleTimeChange(e, weekday, index)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </FormControl>
                    <FormControl variant='outlined' margin='dense' fullWidth>
                      <TextField
                        label='End Time'
                        name='endTime'
                        type='time'
                        variant='outlined'
                        size='small'
                        fullWidth
                        disabled={is24h}
                        value={endTime}
                        onChange={(e) => handleTimeChange(e, weekday, index)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </FormControl>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={is24h}
                          onChange={(e) => handle24h(e, weekday, index)}
                        />
                      )}
                      label='24H'
                    />
                    <DeleteIconButton
                      color='secondary'
                      onClick={() => handleDelete(weekday, index)}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </CollapsibleList>
  )
}

const useStyles = makeStyles(theme => ({
  weekdayTitleWrap: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
  },
}))
